import { isoLocal } from './dates'
import type { Appointment } from './appointments'
import type { LetterTask } from './letters'
import { remainingCents, type Payable } from './payments'

/**
 * The month grid.
 *
 * spec.md §18: daily *totals*, not stacked items. A month of thirty rows of
 * supplier names is unreadable on a phone; a month of thirty numbers tells you
 * where the heavy days are at a glance, which is the only question a calendar
 * answers better than a list.
 *
 * Payments and deadlines are distinguished by **shape and label, never by
 * colour alone** — the grid has to survive colour-blindness and greyscale, and
 * a red dot that means "deadline" to one person and nothing to another is not
 * a distinction at all.
 *
 * Everything here is pure. The month arithmetic is the part that goes wrong in
 * February, so it is testable without a database or a browser in the way.
 */

export interface DayCell {
  date: string
  inMonth: boolean
  isToday: boolean
  /** Money due on this day. Committed only — estimates are counted apart. */
  dueCents: number
  estimatedCents: number
  /** Money you *intend* to move on this day. Different from due, never merged. */
  plannedCents: number
  payments: number
  deadlines: number
  /** Things you have to be at. Counted apart: an appointment is not a task. */
  appointments: number
}

// One definition, in core/dates.ts, re-exported here because the calendar was
// where this was first got right and callers already import it from here.
export { isoLocal }

export function addMonths(iso: string, delta: number): string {
  const [y, m] = iso.split('-').map(Number) as [number, number, number]
  // Day 1 before shifting: asking for "31 January plus one month" is how you
  // get 3 March.
  const d = new Date(y, m - 1 + delta, 1)
  return isoLocal(d)
}

export function monthLabel(iso: string): string {
  const [y, m] = iso.split('-').map(Number) as [number, number, number]
  return new Intl.DateTimeFormat('nl-BE', { month: 'long', year: 'numeric' }).format(
    new Date(y, m - 1, 1),
  )
}

/**
 * Six weeks of cells, Monday-first, covering the given month.
 *
 * Always six rows so the grid does not change height as you page through the
 * year — a layout that jumps is a layout people stop trusting.
 */
export function buildMonth(
  anchorIso: string,
  payables: Payable[],
  tasks: LetterTask[],
  appointments: Appointment[] = [],
  today = new Date(),
): DayCell[] {
  const [y, m] = anchorIso.split('-').map(Number) as [number, number, number]
  const first = new Date(y, m - 1, 1)

  // getDay() is Sunday-based; Belgium starts its weeks on Monday.
  const lead = (first.getDay() + 6) % 7
  const start = new Date(y, m - 1, 1 - lead)

  const todayIso = isoLocal(today)
  const cells: DayCell[] = []

  const dueBy = new Map<string, Payable[]>()
  const plannedBy = new Map<string, Payable[]>()
  for (const p of payables) {
    if (p.state === 'paid' || p.state === 'cancelled') continue
    push(dueBy, p.dueDate, p)
    if (p.plannedDate) push(plannedBy, p.plannedDate, p)
  }

  const deadlinesBy = new Map<string, LetterTask[]>()
  for (const t of tasks) {
    if (t.doneAt || !t.dueDate) continue
    push(deadlinesBy, t.dueDate, t)
  }

  const apptBy = new Map<string, Appointment[]>()
  for (const a of appointments) push(apptBy, a.onDate, a)

  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    const iso = isoLocal(d)
    const due = dueBy.get(iso) ?? []

    cells.push({
      date: iso,
      inMonth: d.getMonth() === m - 1,
      isToday: iso === todayIso,
      dueCents: due.filter((p) => !p.isEstimate).reduce((s, p) => s + remainingCents(p), 0),
      estimatedCents: due.filter((p) => p.isEstimate).reduce((s, p) => s + p.amountCents, 0),
      plannedCents: (plannedBy.get(iso) ?? []).reduce((s, p) => s + remainingCents(p), 0),
      payments: due.length,
      deadlines: (deadlinesBy.get(iso) ?? []).length,
      appointments: (apptBy.get(iso) ?? []).length,
    })
  }

  return cells
}

/**
 * What one day holds.
 *
 * Due and planned are kept apart deliberately (§18): they are different
 * claims — one is what someone else expects, the other is what you decided —
 * and conflating them is how a day looks twice as heavy as it is.
 */
export interface DayDetail {
  due: Payable[]
  planned: Payable[]
  deadlines: LetterTask[]
  appointments: Appointment[]
}

export function detailFor(
  iso: string,
  payables: Payable[],
  tasks: LetterTask[],
  appointments: Appointment[] = [],
): DayDetail {
  const open = payables.filter((p) => p.state !== 'paid' && p.state !== 'cancelled')
  return {
    due: open.filter((p) => p.dueDate === iso),
    // A payment due today that you also planned for today belongs in one list,
    // not both.
    planned: open.filter((p) => p.plannedDate === iso && p.dueDate !== iso),
    deadlines: tasks.filter((t) => !t.doneAt && t.dueDate === iso),
    appointments: appointments.filter((a) => a.onDate === iso),
  }
}

/**
 * How dark a day should read. Relative to the busiest day in view, because an
 * absolute scale would paint a quiet month entirely white and a restaurant's
 * month entirely black.
 */
export function weightOf(cell: DayCell, maxCents: number): number {
  const total = cell.dueCents + cell.estimatedCents
  if (total <= 0 || maxCents <= 0) return 0
  return Math.min(1, 0.25 + 0.75 * (total / maxCents))
}

function push<T>(map: Map<string, T[]>, key: string, value: T): void {
  const list = map.get(key)
  if (list) list.push(value)
  else map.set(key, [value])
}

/**
 * The next weeks, as a list.
 *
 * The grid answers "which days are heavy". It cannot answer "what is next",
 * because next is often four days away and sometimes in the following month —
 * and on the 28th the grid shows you three days and then a wall. That is the
 * week you most need to see past.
 *
 * So this is a second reading of the same data, not a second feature: days
 * that have something on them, in order, across month boundaries, with nothing
 * in between. Rule 23 at the level of a whole view — an empty day is not a row.
 *
 * Overdue is deliberately first and deliberately together. Something that was
 * due last Tuesday is not "in the past"; it is the most present thing you have,
 * and a list that puts it above today is telling the truth about your week.
 */
export interface AgendaDay {
  date: string
  /** Before today and still open. The list shows these first, grouped. */
  overdue: boolean
  isToday: boolean
  due: Payable[]
  planned: Payable[]
  tasks: LetterTask[]
  appointments: Appointment[]
  dueCents: number
}

export function buildAgenda(
  payables: Payable[],
  tasks: LetterTask[],
  appointments: Appointment[] = [],
  today = isoLocal(new Date()),
  days = 60,
): AgendaDay[] {
  const horizon = addDays(today, days)
  const open = payables.filter((p) => p.state !== 'paid' && p.state !== 'cancelled')
  const dates = new Set<string>()

  for (const p of open) {
    if (p.dueDate && p.dueDate <= horizon) dates.add(p.dueDate)
    if (p.plannedDate && p.plannedDate <= horizon) dates.add(p.plannedDate)
  }
  for (const t of tasks) {
    if (!t.doneAt && t.dueDate && t.dueDate <= horizon) dates.add(t.dueDate)
  }
  for (const a of appointments) {
    if (a.onDate <= horizon) dates.add(a.onDate)
  }

  return [...dates]
    .sort()
    .map((date) => {
      const detail = detailFor(date, payables, tasks, appointments)
      return {
        date,
        overdue: date < today,
        isToday: date === today,
        due: detail.due,
        planned: detail.planned,
        tasks: detail.deadlines,
        appointments: detail.appointments,
        dueCents: detail.due.reduce((sum, p) => sum + remainingCents(p), 0),
      }
    })
    .filter(
      (day) =>
        day.due.length + day.planned.length + day.tasks.length + day.appointments.length > 0,
    )
}

/** Days added to an ISO date, staying on dates and away from timezones. */
export function addDays(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  const at = new Date(Date.UTC(y, m - 1, d + delta))
  return at.toISOString().slice(0, 10)
}
